import { z } from "zod";

export const CALENDAR_TIMEZONE = "Europe/Berlin";
export const CALENDAR_MAX_RANGE_DAYS = 370;
export const CALENDAR_MAX_ITEMS = 2_000;
export const CALENDAR_TIMELINE_MAX_PROJECTS = 75;

export const calendarSourceTypeSchema = z.enum([
  "event",
  "project_date",
  "time_off",
  "company_holiday",
  "bank_holiday",
  "call_history",
]);

export const calendarItemKindSchema = z.enum([
  "event",
  "call",
  "deadline",
  "project_date",
  "absence",
  "holiday",
  "call_history",
]);

export const calendarResponseStatusSchema = z.enum(["pending", "accepted", "declined"]);
export const calendarScopeSchema = z.enum(["personal", "company"]);
export const calendarCallTypeSchema = z.enum(["audio", "video"]);

export const calendarAttendeeSchema = z.object({
  userId: z.number().int().positive(),
  name: z.string().nullable(),
  response: calendarResponseStatusSchema,
});

export const calendarPulseLinkSchema = z.object({
  pulseId: z.number().int().positive(),
  projectId: z.string().nullable(),
  title: z.string().nullable(),
});

export const calendarItemSchema = z.object({
  id: z.string(),
  sourceType: calendarSourceTypeSchema,
  sourceId: z.string(),
  kind: calendarItemKindSchema,
  title: z.string(),
  startAt: z.string(),
  endAt: z.string(),
  allDay: z.boolean(),
  timezone: z.string().nullable(),
  projectId: z.string().nullable(),
  projectName: z.string().nullable(),
  projectThumbnail: z.string().nullable(),
  ownerUserId: z.number().int().nullable(),
  busy: z.boolean(),
  color: z.string().nullable(),
  version: z.number().int().nullable(),
  canEdit: z.boolean(),
  canCancel: z.boolean(),
  attendees: z.array(calendarAttendeeSchema).nullable(),
  pulseLinks: z.array(calendarPulseLinkSchema),
  callType: calendarCallTypeSchema.nullable().optional(),
  // Server-derived from participant/source metadata, not project association.
  // Scheduling currently accepts internal user IDs only. Future external
  // scheduling must populate this flag from its authoritative guest records.
  hasExternalParticipants: z.boolean().optional(),
  scope: calendarScopeSchema.optional(),
  status: z.string().optional(),
  response: calendarResponseStatusSchema.nullable().optional(),
  organizerUserId: z.number().int().nullable().optional(),
  busyUserId: z.number().int().nullable().optional(),
  /** Aggregate/borrowed agenda rows can be displayed without mutation controls. */
  readOnly: z.boolean().optional(),
});

const calendarDateValueSchema = z.string().min(1).max(64);

export const calendarEventInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  startAt: calendarDateValueSchema,
  endAt: calendarDateValueSchema,
  allDay: z.boolean(),
  timezone: z.string().trim().min(1).max(100),
  scope: calendarScopeSchema,
  ownerUserId: z.number().int().positive(),
  projectId: z.string().length(7).nullable(),
  kind: z.enum(["event", "call"]),
  callType: calendarCallTypeSchema,
  busy: z.boolean(),
  attendeeUserIds: z.array(z.number().int().positive()).max(100),
}).strict();

export const calendarEventPatchSchema = calendarEventInputSchema.extend({
  version: z.number().int().positive(),
}).strict();

export const calendarEventDeleteSchema = z.object({
  version: z.number().int().positive(),
}).strict();

export const calendarEventResponseSchema = z.object({
  response: z.enum(["accepted", "declined"]),
}).strict();

export const calendarSubscriptionScopeSchema = calendarScopeSchema;

export type CalendarSourceType = z.infer<typeof calendarSourceTypeSchema>;
export type CalendarItemKind = z.infer<typeof calendarItemKindSchema>;
export type CalendarResponseStatus = z.infer<typeof calendarResponseStatusSchema>;
export type CalendarScope = z.infer<typeof calendarScopeSchema>;
export type CalendarAttendee = z.infer<typeof calendarAttendeeSchema>;
export type CalendarPulseLink = z.infer<typeof calendarPulseLinkSchema>;
export type CalendarItem = z.infer<typeof calendarItemSchema>;
export type CalendarEventInput = z.infer<typeof calendarEventInputSchema>;

export interface CalendarItemsResponse {
  items: CalendarItem[];
  truncated: boolean;
}

export interface CalendarOptions {
  users: Array<{ id: number; name: string }>;
  projects: Array<{ id: string; name: string; thumbnail: string | null }>;
  isAdmin: boolean;
}

export interface CalendarHistoryResponse {
  items: CalendarItem[];
  nextCursor: string | null;
}

export interface CalendarSubscriptionSummary {
  id: string;
  scope: CalendarScope;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface CalendarSubscriptionCreated extends CalendarSubscriptionSummary {
  /** Returned exactly once; only the SHA-256 token hash is persisted. */
  url: string;
}
